import { useCallback, useContext } from 'react';
import { useFindHotelMatchLazyQuery } from '@graphql/generated';
import { AnalyticsContext } from '@shared/core';
import { addError } from '@shared/utils/logger';
import { withWindow } from '@shared/utils/withWindow';
import { useDisclosure } from '@withjoy/joykit';
import { ensureHttpsPrefix } from '@shared/utils/urlHelpers';

interface UseHotelSuggestionDialogProps {
  hrefProp: string | undefined;
  isAllowListedUrl: boolean | undefined;
}

export const useHotelSuggestionDialog = ({ hrefProp, isAllowListedUrl }: UseHotelSuggestionDialogProps) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { isOpen: isOpenLinkConfirmationDialog, onClose: onCloseLinkConfirmationDialog, onOpen: onOpenLinkConfirmationDialog } = useDisclosure();

  const analytics = useContext(AnalyticsContext);

  const openHotelSuggestionDialog = useCallback(() => {
    onOpen();
    analytics.page({ action: 'enter', category: 'website', pagePrefix: 'guest', page: 'guestRateAvailableModal', extraPageInfo: { name: 'JoyHotelLink' } });
  }, [onOpen, analytics]);

  const openSafeLinkUrl = useCallback(() => {
    analytics.track({
      action: 'LinkInteracted',
      category: 'safeLink',
      extraInfo: { actionType: 'click', url: hrefProp, isAllowListedUrl }
    });
    withWindow(global => {
      global.open(hrefProp, '_blank');
    });
  }, [analytics, hrefProp, isAllowListedUrl]);

  const href = ensureHttpsPrefix(hrefProp?.trim() || '');

  const [findHotelMatch, { data: hotelMatchData, called: hotelMatchCalled, loading: isLoadingHotelMatchData }] = useFindHotelMatchLazyQuery({
    onCompleted: data => {
      if (data.findHotelMatch) {
        openHotelSuggestionDialog();
      } else {
        onClose();
        isAllowListedUrl ? openSafeLinkUrl() : onOpenLinkConfirmationDialog();
      }
    },
    onError: error => {
      addError(error, { href, type: 'findHotelMatch' });
      onClose();
      isAllowListedUrl ? openSafeLinkUrl() : onOpenLinkConfirmationDialog();
    },
    batchMode: 'fast'
  });

  return {
    isOpen,
    onOpen,
    onClose,
    isOpenLinkConfirmationDialog,
    onOpenLinkConfirmationDialog,
    onCloseLinkConfirmationDialog,
    openSafeLinkUrl,
    openHotelSuggestionDialog,
    findHotelMatch,
    hotelMatchData,
    hotelMatchCalled,
    isLoadingHotelMatchData,
    href
  };
};
